import { useEffect, useState } from "react";
import { Sparkles } from "lucide-react";
import Card from "@/components/common/Card";
import IconPicker from "@/components/common/IconPicker";
import BuildConditions from "@/components/segments/conditions/BuildConditions";

const AI_STATUS_STEPS = [
  "Understanding your description",
  "Identifying key personas",
  "Finding relevant behaviors",
  "Building look-alike models",
];

const NAME_MAX = 100;
const DESCRIPTION_MAX = 250;

const LABEL_CLASS = "text-base! font-semibold! text-(--text-primary)!";

export default function Step1Define({
  form,
  updateField,
  onGenerate,
  isGenerating,
  aiError,
  aiSummary,
  definition,
  onSetGroup,
  fieldOptionConfig,
}) {
  const [revealedSteps, setRevealedSteps] = useState(0);

  useEffect(() => {
    if (!isGenerating) return undefined;
    setRevealedSteps(0);
    let i = 0;
    const interval = setInterval(() => {
      i += 1;
      setRevealedSteps(i);
      if (i >= AI_STATUS_STEPS.length) clearInterval(interval);
    }, 650);
    return () => clearInterval(interval);
  }, [isGenerating]);

  const description = form.description ?? "";

  return (
    <Card className="flex flex-col gap-4">
      <div className="flex flex-col gap-2 [&>.form-group-modal]:mb-0">
        <div className="form-group-modal">
          <label htmlFor="segment-name" className={LABEL_CLASS}>
            Audience name <span className="text-(--status-danger)">*</span>
          </label>
          <div className="flex items-center gap-3">
            <IconPicker
              iconOnly
              value={form.icon ?? ""}
              onChange={(next) => updateField("icon", next || null)}
            />
            <div className="relative flex-1">
              <input
                id="segment-name"
                className="w-full pr-16"
                value={form.name}
                maxLength={NAME_MAX}
                onChange={(e) =>
                  updateField("name", e.target.value.slice(0, NAME_MAX))
                }
                placeholder="e.g. Wellness Enthusiasts"
                required
              />
              <span className="pointer-events-none absolute right-3 top-1/2 -translate-y-1/2 text-xs text-(--text-muted)">
                {form.name.length} / {NAME_MAX}
              </span>
            </div>
          </div>
        </div>

        <div className="form-group-modal">
          <div className="mb-2.5 flex items-center justify-between gap-3">
            <label
              htmlFor="segment-description"
              className={`mb-0! ${LABEL_CLASS}`}
            >
              Description{" "}
              <span className="text-sm! font-normal! text-(--text-muted)!">
                (optional)
              </span>
            </label>
            <button
              type="button"
              onClick={onGenerate}
              disabled={isGenerating || !description.trim()}
              className="inline-flex h-8 items-center gap-1.5 rounded-(--radius-md) bg-(--brand-primary) px-3 text-xs font-semibold text-(--brand-primary-contrast) hover:opacity-90 disabled:cursor-not-allowed disabled:opacity-60"
            >
              <Sparkles size={13} strokeWidth={2} />
              {isGenerating ? "Generating…" : "Generate with AI"}
            </button>
          </div>
          <div className="relative">
            <textarea
              id="segment-description"
              value={description}
              onChange={(e) =>
                updateField(
                  "description",
                  e.target.value.slice(0, DESCRIPTION_MAX),
                )
              }
              rows={2}
              maxLength={DESCRIPTION_MAX}
              placeholder="Describe this audience — this also guides the AI builder."
              className="block w-full resize-y rounded-(--radius-md) border border-(--border-subtle) bg-(--surface-1) p-3 pr-16 text-sm text-(--text-primary) placeholder:text-(--text-muted) focus:border-(--brand-primary) focus:outline-none focus:ring-2 focus:ring-(--brand-primary)/20"
            />
            <span className="pointer-events-none absolute bottom-2 right-3 text-xs text-(--text-muted)">
              {description.length} / {DESCRIPTION_MAX}
            </span>
          </div>
          {revealedSteps > 0 && (
            <ul className="mt-3 flex flex-wrap gap-x-4 gap-y-1.5 text-xs">
              {AI_STATUS_STEPS.slice(0, revealedSteps).map((step) => (
                <li key={step} className="flex items-center gap-1.5">
                  <span className="flex h-4 w-4 shrink-0 items-center justify-center rounded-(--radius-pill) bg-(--brand-primary) text-[10px] font-bold text-(--brand-primary-contrast)">
                    ✓
                  </span>
                  <span className="text-(--text-secondary)">{step}</span>
                </li>
              ))}
            </ul>
          )}
          {aiError && (
            <p className="mt-2 text-sm text-(--status-danger)">{aiError}</p>
          )}
          {aiSummary && !aiError && (
            <p className="mt-2 text-sm text-(--status-success)">{aiSummary}</p>
          )}
        </div>
      </div>

      <BuildConditions
        definition={definition}
        onSetGroup={onSetGroup}
        fieldOptionConfig={fieldOptionConfig}
      />
    </Card>
  );
}
